import BaseService from '../../base/base_service';
import { PRIORITY_LEVEL } from '../overlay_manager/overlay_manager_levels';
import { SYSTEM_EVENTS } from '../../events/events';
import { GNWP_SOURCES } from '../../../../app-react/services/gnwp/gnwp-consts';
import Store from '../../utils/store';
import Device from '../../utils/device/device';
import Event, { SystemEventSubscription } from '../../utils/event';
import DOM from '../../utils/dom';
import HTTPLoader from '../../utils/http_loader';
import {
  IGameQuestionnaireBuilder,
  IGamesQuestionnaireBuilderContext,
  IGamesQuestionnaireBuilderParams,
  IGamesQuestionnaireServiceParams,
} from './games_questionnaire_service_interface';
import { IElasticEventData } from '../../../../common/interfaces/structured-logging-service-interface';
import _ from '../../utils/underscore';

const GQ_LOCAL_STORAGE_KEY = "shouldDisplayGamesQuestionnaire";
const GQ_SCRIPT_ID = "games_questionnaire_loader";
const GQ_CONTAINER_ID = "GamesQuestionnaireContainer";

export default class GamesQuestionnaireService extends BaseService{

  static CLASS = 'GamesQuestionnaireService';

  private _initParams: IGamesQuestionnaireServiceParams = null;
  private _cashierSubscription: SystemEventSubscription = null;
  private _membershipSubscription: SystemEventSubscription = null;
  private _loginSubscription: SystemEventSubscription = null;
  private _loader: HTMLElement = null;
  private _gqContainer: HTMLElement = null;
  private _isListeningToCashier: boolean = false;
  private _gamesQuestionnaireBuilder: IGameQuestionnaireBuilder = null;
  private _startTimeMs: number = null;

  init(params: IGamesQuestionnaireServiceParams) {
    super.init(params);
    this._initParams = params;
  }

    protected shouldApplyFeatureToggleABTesting(): boolean{
    return true;
  }

  private _isEnabledForCurrentRegulation(): boolean{
    const currentRegulationCode = this.serviceFactory.regulationService().getRegulationCode();
    if (this._initParams.sourceUrl && this._initParams.enabledForRegulation && this._initParams.enabledForRegulation[currentRegulationCode]){
      return true;
    }
    return false;
  }

    protected toggleFeatureOn(params: any): void{
    if(this.featureEnabled){
      return;
    }
    if(!this._isEnabledForCurrentRegulation()){
      return;
    }
     this.featureEnabled = true;
     this._startListeningToCashier();
  }

  onInitializationCompleted(): Promise<any> {
    if (this._isEnabledForCurrentRegulation())
    {
      if(Store.get(GQ_LOCAL_STORAGE_KEY))
        this._showOverlay();
      else
        this._subscribe();
    }
    return super.onInitializationCompleted();
  }

  private _subscribe(): void{
    this._membershipSubscription = this.onSystemEvent(SYSTEM_EVENTS.REGISTRATION_COMPLETED, this._listenToLogin);
  }

  private _listenToLogin(): void{
    this._loginSubscription = this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._startListeningToCashier);
  }

  private _startListeningToCashier(): void{
    if(this._isListeningToCashier){
      return;
    }

    if(!this._loginSubscription){
      return;
    }

    if(this._loginSubscription && this._loginSubscription.off)
      this._loginSubscription.off();

    if(this.featureToggle()){

      if(this._membershipSubscription && this._membershipSubscription.off){
        this._membershipSubscription.off();
      }

      Store.set(GQ_LOCAL_STORAGE_KEY, true);

      this._cashierSubscription = this.onSystemEvent(SYSTEM_EVENTS.EXTERNAL_LINK_CLOSED, this._handleCloseCashier);
      this._isListeningToCashier = true;

      this._sendElasticAnalytics({
        action: "listening_cashier_close"
      });
    }
  }

  private _handleCloseCashier(event: any): void{
    if(event !== GNWP_SOURCES.CASHIER)
      return;

    if(this._cashierSubscription && this._cashierSubscription.off)
      this._cashierSubscription.off();

    this._showOverlay();
  }

  private async _initializeScript(): Promise<void>{
    this.serviceFactory.scriptLoaderService().loadScript({
      url: this._initParams.sourceUrl,
      tagId: GQ_SCRIPT_ID
    })
      .then( () => this._getContextObject())
      .then(contextObject => {
      const params: IGamesQuestionnaireBuilderParams = {
        context: contextObject,
        onReady: this.onReadyHandler.bind(this),
        onError: this.onErrorHandler.bind(this),
        onComplete: this.onCompleteHandler.bind(this)
      };
      this._gamesQuestionnaireBuilder = this.serviceFactory.windowService().getGlobalProperty("GamesQuestionnaire");
      this._gamesQuestionnaireBuilder(params).render('#' + GQ_CONTAINER_ID);
    });
  }

  private _showOverlay(): void{
    Store.del(GQ_LOCAL_STORAGE_KEY);
    this._startTimeMs = Date.now();

    this._gqContainer = DOM.createElement();
    DOM.addAttribute(this._gqContainer, "id", GQ_CONTAINER_ID);

    let overlayClass = "games-questionnaire-overlay";
    if(Device.isMobile())
      overlayClass += "-mobile";

    const overlayOptions = {
      el: this._gqContainer,
      additionalClasses: [overlayClass],
      isCloseBtnAllowed: false,
      priority: PRIORITY_LEVEL.GAMES_QUESTIONNAIRE
    };

    Event.fire(SYSTEM_EVENTS.OPEN_OVERLAY, overlayOptions);

    this._displayLoader();

    this._initializeScript();
  }

  private _displayLoader(): void{
    this._loader = DOM.createElement();
    DOM.addClass(this._loader,"cgp-loading");
    DOM.addClass(this._loader, "cgp-external-link-loader");
    DOM.append(this._gqContainer, this._loader);
    HTTPLoader.show();
  }

  private async _getContextObject(): Promise<IGamesQuestionnaireBuilderContext>{
    const userService = this.serviceFactory.userService();
    const token = await userService.renewLoginToken().then(response => response.token);
    const skinService = this.serviceFactory.skinService();
    const languageService = this.serviceFactory.languageService();
    const langISO3 = languageService.getISO3();

    return {
      userInfo: token,
      brandId: skinService.getBrandID(),
      subBrandId: skinService.getSubBrandID(),
      productPackage: skinService.getProductPackageID(),
      systemId: parseInt(skinService.getPlatformSystemID()),
      lang: langISO3,
      clientPlatform: parseInt(skinService.getClientPlatform()),
      clientVersion: skinService.getVersion()
    };
  }

  onReadyHandler(): void{
    const timeElapsedSecs: number = (Date.now() - this._startTimeMs)/1000;

    this._sendElasticAnalytics({
      action: "displayed",
      value: timeElapsedSecs.toFixed(2),
    });

    HTTPLoader.hide();
  }

  onErrorHandler(err: any): void{
    this.logger.error('Error opening games questionnaire', err);
    const timeElapsedSecs: number = (Date.now() - this._startTimeMs)/1000;
    this._sendElasticAnalytics({
      action: "error",
      label: JSON.stringify(err).substr(0, 100),
      value: timeElapsedSecs.toFixed(2),
    }, true);

    this._closeGamesQuestionnaire();
  }

  onCompleteHandler(arePreferencesSaved: boolean): void{
    if(arePreferencesSaved){
      this.serviceFactory.aiListService().clearGamesRecommendationsCache();
      this._fireGamesQuestionariesCompleted();
    }
    const label = arePreferencesSaved ? "preferences_saved" : "preferences_not_saved";
    this._sendElasticAnalytics({
      action: "completed",
      label: label,
    });
    this._closeGamesQuestionnaire();
  }

  private _fireGamesQuestionariesCompleted(): void{
    Event.fire(SYSTEM_EVENTS.GAMES_QUESTIONNAIRE_COMPLETED);
  }

  private _closeGamesQuestionnaire(): void{
    Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {
      priority: PRIORITY_LEVEL.GAMES_QUESTIONNAIRE
    });
  }

  private _sendElasticAnalytics(elasticData: any, isError: boolean = false): void{
    const data: IElasticEventData = this._buildElasticData(elasticData);
    if(isError){
      this.serviceFactory.structuredLoggingService().getLogger(data).error();
    }else{
      this.serviceFactory.structuredLoggingService().getLogger(data).info();
    }
  }

  private _buildElasticData({action, label, value}): IElasticEventData {
    const data: IElasticEventData = {
      CGPEventCategory: 'Games_Questionnaire',
      CGPEventAction: action
    };
    if (label){
      data.CGPEventLabel = label;
    }
    if (value) {
      const valueAsNumber = Number(value);
      if (_.isNumber(valueAsNumber)) {
        data.CGPEventValueAsNumber = valueAsNumber;
      } else {
        data.CGPEventValue = value;
      }
    }
    return data;
  }
}